import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { DOZEN_API_URL } from '../config';
import { DOZEN_APP } from '../apps';
import AppShell from './AppShell';
import LetterGrid, { gridColumnsFor } from './LetterGrid';
import GuessInput, { lettersLabel } from './GuessInput';
import DozenProgress from './DozenProgress';
import { shuffleArray } from '../utils/shuffle';
import {
  assignIndicesForGuess,
  guessFromIndices,
  togglePick,
  hasFinePointer
} from '../utils/letterPicking';
import {
  formatClock,
  formatCountdown,
  formatPuzzleDate,
  pointsLabel
} from '../utils/dozen';
import './DozenGame.css';

const LOW_TIME_SECONDS = 5;
const WRONG_FLASH_MS = 450;
const FETCH_RETRY_MS = 2000;

function outcomeTitle(reveal) {
  if (reveal.outcome === 'solved') return `+${pointsLabel(reveal.points)}`;
  return 'Время вышло';
}

function RevealCard({ reveal, bonus }) {
  const otherAnswer = reveal.answer && reveal.answer !== reveal.word ? reveal.answer : null;
  const alternatives = reveal.alternatives.filter((w) => w !== otherAnswer);
  return (
    <div className={`panel dozen-reveal dozen-reveal--${reveal.outcome}`}>
      <p className="dozen-reveal__title">{outcomeTitle(reveal)}</p>
      <p className="dozen-reveal__word">{otherAnswer || reveal.word}</p>
      {otherAnswer && <p className="dozen-reveal__meta">Загадано: {reveal.word}</p>}
      {alternatives.length > 0 && (
        <p className="dozen-reveal__meta">
          {reveal.outcome === 'solved' ? 'Тоже подошло бы' : 'Подходило'}: {alternatives.join(', ')}
        </p>
      )}
      {reveal.final && bonus > 0 && (
        <p className="dozen-reveal__bonus">Вся дюжина! +{pointsLabel(bonus)}</p>
      )}
    </div>
  );
}

function ResultsCard({ state, nextLabel }) {
  const total = state.wordLengths.length;
  const headline = state.solvedCount === total ? 'Вся дюжина!' : `${state.solvedCount} из ${total}`;
  return (
    <div className="dozen-results">
      <DozenProgress
        wordLengths={state.wordLengths}
        progress={state.progress}
        size="lg"
      />
      <h2 className="dozen-results__headline">{headline}</h2>
      <div className="dozen-chips">
        <span className="dozen-chip">{pointsLabel(state.score)}</span>
        {state.bonus > 0 && (
          <span className="dozen-chip">Бонус +{state.bonus}</span>
        )}
        <span className="dozen-chip">Время {formatClock(state.totalTimeMs)}</span>
        {state.rank ? (
          <span className="dozen-chip">
            Место {state.rank} из {state.participants}
          </span>
        ) : null}
      </div>
      {nextLabel && <p className="next-word">{nextLabel}</p>}

      <ol className="dozen-results__list">
        {state.results.map((r) => {
          const otherAnswer = r.answer && r.answer !== r.word ? r.answer : null;
          return (
            <li
              key={r.position}
              className={`dozen-result dozen-result--${r.outcome === 'solved' ? 'solved' : 'missed'}`}
            >
              <span className="dozen-result__num" title={lettersLabel(r.length)}>
                {r.position + 1}
              </span>
              <span className="dozen-result__words">
                <strong>{otherAnswer || r.word}</strong>
                {otherAnswer && <small>загадано {r.word}</small>}
                {!otherAnswer && r.alternatives.length > 0 && (
                  <small>или {r.alternatives.join(', ')}</small>
                )}
              </span>
              <span className="dozen-result__points">
                {r.outcome === 'solved' ? `+${r.points}` : '—'}
                <small>{r.outcome === 'solved' ? formatClock(r.timeMs) : 'время'}</small>
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

const API_BASE = `${DOZEN_API_URL}/game`;

function DozenGame() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());
  const [letters, setLetters] = useState('');
  const [guess, setGuess] = useState('');
  const [picked, setPicked] = useState([]);
  const [message, setMessage] = useState('');
  const [shake, setShake] = useState(false);
  const [busy, setBusy] = useState(false);
  const [shuffleUsed, setShuffleUsed] = useState(false);
  const offsetRef = useRef(0);
  const inputRef = useRef(null);
  const shakeTimerRef = useRef(null);
  const busyRef = useRef(false);
  const flashRef = useRef(false);
  const wrongTimerRef = useRef(null);
  const [wrongFlash, setWrongFlash] = useState(false);

  const serverNow = now + offsetRef.current;

  const applyState = useCallback((data) => {
    if (!data) return;
    offsetRef.current = data.serverNow - Date.now();
    setNow(Date.now());
    setState(data);
  }, []);

  // Keep asking for the puzzle we are on, so the next publish doesn't cut a game short.
  const puzzleParams = useCallback(
    (current) =>
      current && current.phase !== 'intro' ? { puzzleId: current.puzzleId } : {},
    []
  );

  const stateRef = useRef(null);
  stateRef.current = state;
  const retryTimerRef = useRef(null);

  const fetchState = useCallback(
    async ({ keepPuzzle = true } = {}) => {
      window.clearTimeout(retryTimerRef.current);
      try {
        const params = keepPuzzle ? puzzleParams(stateRef.current) : {};
        const { data } = await axios.get(`${API_BASE}/state`, { params });
        applyState(data);
        setError('');
      } catch (err) {
        setError(err.response?.data?.error || 'Ошибка загрузки');
        // Phase changes are only scheduled from fresh state, so keep trying mid-game.
        if (stateRef.current) {
          retryTimerRef.current = window.setTimeout(
            () => fetchState({ keepPuzzle }),
            FETCH_RETRY_MS
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [applyState, puzzleParams]
  );

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 200);
    return () => window.clearInterval(id);
  }, []);

  // Phones pause timers while locked: catch up as soon as the page is visible again.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const phaseNow = stateRef.current?.phase;
      fetchState({ keepPuzzle: phaseNow === 'playing' || phaseNow === 'reveal' });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchState]);

  useEffect(
    () => () => {
      window.clearTimeout(shakeTimerRef.current);
      window.clearTimeout(wrongTimerRef.current);
      window.clearTimeout(retryTimerRef.current);
    },
    []
  );

  const phase = state?.phase;
  const current = state?.current || null;
  const positionKey = `${state?.puzzleId}:${phase}:${current?.position ?? ''}`;

  // New word on screen: reset the board.
  useEffect(() => {
    window.clearTimeout(wrongTimerRef.current);
    flashRef.current = false;
    setWrongFlash(false);
    setLetters(current?.letters || '');
    setGuess('');
    setPicked([]);
    setMessage('');
    setShuffleUsed(false);
    if (current && hasFinePointer()) {
      window.requestAnimationFrame(() => inputRef.current?.focus());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionKey]);

  // Ask the server for the next phase exactly when it is due.
  useEffect(() => {
    if (!state) return undefined;
    let dueAt = null;
    let keepPuzzle = true;
    if (state.phase === 'playing') {
      dueAt = state.current.deadline + state.graceMs + 150;
    } else if (state.phase === 'reveal') {
      dueAt = state.reveal.nextAt + 50;
    } else if (state.nextPuzzleTime) {
      dueAt = new Date(state.nextPuzzleTime).getTime() + 3000;
      keepPuzzle = false;
    }
    if (!dueAt) return undefined;
    const delay = Math.max(0, dueAt - (Date.now() + offsetRef.current));
    // setTimeout overflows past ~24.8 days; the next puzzle is always within a day.
    const id = window.setTimeout(() => fetchState({ keepPuzzle }), Math.min(delay, 2 ** 31 - 1));
    return () => window.clearTimeout(id);
  }, [state, fetchState]);

  const remainingMs = current ? current.deadline - serverNow : 0;
  const secondsLeft = Math.max(0, Math.ceil(remainingMs / 1000));
  const timeUp = phase === 'playing' && remainingMs <= 0;
  const usedIndices = useMemo(() => new Set(picked), [picked]);

  const post = async (path, body) => {
    const { data } = await axios.post(`${API_BASE}/${path}`, {
      ...puzzleParams(stateRef.current),
      ...body
    });
    return data;
  };

  const handleStart = async () => {
    setBusy(true);
    try {
      applyState(await post('start'));
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Не удалось начать');
    } finally {
      setBusy(false);
    }
  };

  const triggerShake = () => {
    window.clearTimeout(shakeTimerRef.current);
    setShake(false);
    window.requestAnimationFrame(() => {
      setShake(true);
      shakeTimerRef.current = window.setTimeout(() => setShake(false), 450);
    });
  };

  // Wrong word stays on screen red and shaking for a moment, then clears.
  const flashWrong = () => {
    window.clearTimeout(wrongTimerRef.current);
    flashRef.current = true;
    setWrongFlash(true);
    triggerShake();
    wrongTimerRef.current = window.setTimeout(() => {
      flashRef.current = false;
      setWrongFlash(false);
      setGuess('');
      setPicked([]);
      if (hasFinePointer()) inputRef.current?.focus();
    }, WRONG_FLASH_MS);
  };

  // Submitted automatically once every letter is placed; a miss clears the field.
  const submitGuess = async (raw) => {
    if (!current || busyRef.current || timeUp) return;
    const value = String(raw || '').trim().toUpperCase();
    if (value.length !== current.length) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const data = await post('guess', { position: current.position, guess: value });
      if (data.result === 'wrong') {
        setMessage(data.message || 'Мимо!');
        flashWrong();
      }
      applyState(data.state);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Ошибка при отправке ответа');
      setGuess('');
      setPicked([]);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitGuess(guess);
  };

  const handleLetterClick = (index) => {
    if (!current || timeUp || busyRef.current || flashRef.current) return;
    const next = togglePick(picked, index, current.length);
    if (!next) return;
    const nextGuess = guessFromIndices(letters, next);
    setPicked(next);
    setGuess(nextGuess);
    if (message) setMessage('');
    if (nextGuess.length === current.length) submitGuess(nextGuess);
  };

  const handleGuessChange = (value) => {
    if (!current || busyRef.current || flashRef.current) return;
    const nextGuess = String(value || '').toUpperCase().slice(0, current.length);
    setGuess(nextGuess);
    setPicked(assignIndicesForGuess(letters, nextGuess));
    if (message) setMessage('');
    if (nextGuess.length === current.length) submitGuess(nextGuess);
  };

  const handleBackspace = () => {
    if (flashRef.current) return;
    if (picked.length > 0) {
      const next = picked.slice(0, -1);
      setPicked(next);
      setGuess(guessFromIndices(letters, next));
    } else {
      handleGuessChange(guess.slice(0, -1));
    }
  };

  // One shuffle per word.
  const handleShuffle = () => {
    if (shuffleUsed) return;
    setShuffleUsed(true);
    let shuffled = letters;
    for (let attempt = 0; attempt < 10 && shuffled === letters; attempt++) {
      shuffled = shuffleArray(letters.split('')).join('');
    }
    setLetters(shuffled);
    setPicked(assignIndicesForGuess(shuffled, guess));
  };

  const handleClear = () => {
    setGuess('');
    setPicked([]);
    setMessage('');
  };

  const shellProps = { app: DOZEN_APP };

  if (loading) {
    return (
      <AppShell {...shellProps}>
        <div className="loading">Загрузка...</div>
      </AppShell>
    );
  }

  if (!state) {
    return (
      <AppShell {...shellProps}>
        <div className="game-content">
          <div className="alert alert-error">{error || 'Дюжина ещё не готова'}</div>
          <button type="button" className="btn btn-secondary" onClick={() => fetchState()}>
            Обновить
          </button>
        </div>
      </AppShell>
    );
  }

  const nextMs = state.nextPuzzleTime ? new Date(state.nextPuzzleTime).getTime() - serverNow : 0;
  const nextLabel = nextMs > 0 ? `Следующая дюжина через ${formatCountdown(nextMs)}` : '';
  const dateLabel = formatPuzzleDate(state.publishedAt);
  const tileColumns = Math.max(...state.wordLengths.map(gridColumnsFor));

  return (
    <AppShell {...shellProps}>
      <div className="game-content dozen-content">
        {error && <div className="alert alert-error">{error}</div>}

        {phase === 'intro' && (
          <>
            <p className="game-kicker">{`Дюжина${dateLabel ? ` · ${dateLabel}` : ''}`}</p>
            <DozenProgress wordLengths={state.wordLengths} progress={[]} size="lg" />
            <div className="panel dozen-intro">
              <h2 className="dozen-intro__title">12 слов на скорость</h2>
              <ul>
                <li>Слова от 4 до 8 букв, по нарастающей.</li>
                <li>На каждое — {state.wordTimeMs / 1000} секунд. Сколько секунд осталось, столько и очков.</li>
                <li>Угадали все 12 — ещё +{state.allSolvedBonus} очков.</li>
                <li>Подходит любое слово из этих букв, если оно есть в словаре.</li>
                <li>Можно нажимать буквы или печатать — как удобнее.</li>
                <li>Паузы нет: таймер идёт, даже если закрыть страницу.</li>
              </ul>
              <button
                type="button"
                className="btn btn-primary dozen-intro__start"
                onClick={handleStart}
                disabled={busy}
              >
                Начать
              </button>
            </div>
            {nextLabel && <p className="next-word">{nextLabel}</p>}
          </>
        )}

        {(phase === 'playing' || phase === 'reveal') && (
          <>
            <DozenProgress
              wordLengths={state.wordLengths}
              progress={state.progress}
              currentPosition={current?.position ?? null}
            />

            {phase === 'playing' && current && (
              <>
                <div className="dozen-toolbar">
                  <span className="dozen-toolbar__meta">
                    {current.position + 1}/{state.wordLengths.length} · {lettersLabel(current.length)}
                  </span>
                  <span
                    className={`dozen-timer${secondsLeft <= LOW_TIME_SECONDS ? ' dozen-timer--low' : ''}`}
                    aria-live="polite"
                  >
                    {secondsLeft}
                    <small>с</small>
                  </span>
                  <span className="dozen-toolbar__actions">
                    <button
                      type="button"
                      className="app-icon-button"
                      onClick={handleShuffle}
                      disabled={timeUp || shuffleUsed}
                      aria-label="Перемешать буквы (один раз на слово)"
                      title={shuffleUsed ? 'Уже перемешано' : 'Перемешать (один раз)'}
                    >
                      <svg className="dozen-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <path
                          d="M4 7h3.5c2 0 3.2 1 4.3 2.6l2.4 3.8C15.3 15 16.5 17 18.5 17H20M4 17h3.5c1.3 0 2.2-.5 3-1.3M14.5 8.3c.9-.8 1.8-1.3 3-1.3H20M17.5 4.5 20 7l-2.5 2.5M17.5 14.5 20 17l-2.5 2.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="app-icon-button"
                      onClick={handleClear}
                      disabled={timeUp || !guess}
                      aria-label="Стереть всё"
                      title="Стереть"
                    >
                      <svg className="dozen-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <path
                          d="M9 5h10a1.5 1.5 0 0 1 1.5 1.5v11A1.5 1.5 0 0 1 19 19H9l-6-7 6-7ZM11.5 9.5l5 5M16.5 9.5l-5 5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </span>
                </div>
                <div className="dozen-timebar" aria-hidden="true">
                  <span
                    style={{
                      width: `${Math.max(0, Math.min(1, remainingMs / state.wordTimeMs)) * 100}%`
                    }}
                    className={secondsLeft <= LOW_TIME_SECONDS ? 'dozen-timebar--low' : ''}
                  />
                </div>

                <LetterGrid
                  letters={letters}
                  count={current.length}
                  size="md"
                  tileColumns={tileColumns}
                  className={wrongFlash ? 'dozen-grid--wrong' : ''}
                  usedIndices={usedIndices}
                  onLetterClick={timeUp ? null : handleLetterClick}
                />

                <GuessInput
                  value={guess}
                  length={current.length}
                  onChange={handleGuessChange}
                  onBackspace={handleBackspace}
                  onSubmit={handleSubmit}
                  inputRef={inputRef}
                  shake={shake || wrongFlash}
                  message={timeUp ? '' : message}
                  disabled={timeUp}
                  showSubmit={false}
                >
                  {timeUp && <p className="dozen-hint">Время!</p>}
                </GuessInput>
              </>
            )}

            {phase === 'reveal' && state.reveal && (
              <RevealCard reveal={state.reveal} bonus={state.bonus} />
            )}
          </>
        )}

        {phase === 'finished' && (
          <ResultsCard
            state={state}
            nextLabel={nextLabel}
          />
        )}
      </div>
    </AppShell>
  );
}

export default DozenGame;
